import React from 'react';
import { BattleUnitState, BattleTileState, TurnOrderEntry, HotbarSkill, StatusEffect } from '../types/battle';
import { getTileColor, getTileAtPosition, getUnitAtPosition, getHealthColor } from '../utils/battleUtils';
import { calculateAOEPreview } from '../utils/battleUtils';
import { getUnitSpriteLayers, getTerrainSprite } from '../utils/spriteMap';
import BattleUnitEquipment from './BattleUnitEquipment';

interface BattleGridTilesProps {
  gridWidth: number;
  gridHeight: number;
  tiles: BattleTileState[];
  units: BattleUnitState[];
  selectedUnit: BattleUnitState | null;
  currentTurnUnit: TurnOrderEntry | null;
  validMoves: { x: number; y: number }[];
  validTargets: BattleUnitState[];
  skillTargets: { x: number; y: number }[];
  skillRange: { x: number; y: number }[];
  aoeArea: { x: number; y: number }[];
  weaponRange: { x: number; y: number }[];
  attackMode: boolean;
  skillMode: boolean;
  selectedSkill: HotbarSkill | null;
  itemTargets: BattleUnitState[];
  onTileClick: (x: number, y: number) => void;
  onAoeAreaChange: (area: { x: number; y: number }[]) => void;
}

const getEffectIcon = (effectType: string): string => {
  switch (effectType) {
    case 'Poison':          return '🧪';
    case 'Burn':            return '🔥';
    case 'Invisibility':    return '👻';
    case 'Root':            return '🌿';
    case 'DamageBuff':      return '⚔️';
    case 'DamageReduction': return '🛡️';
    case 'StatBuff':        return '✨';
    case 'Stun':            return '💫';
    default:                return '❓';
  }
};

const getTileTooltip = (tile: BattleTileState | undefined, x: number, y: number): string => {
  if (!tile) return `(${x},${y})`;
  let tooltip = `${tile.type} (${x},${y})`;
  if (tile.movementCost > 1)  tooltip += `\nMovement Cost: ${tile.movementCost}`;
  if (tile.evasionBonus > 0)  tooltip += `\n+${tile.evasionBonus}% Evasion`;
  else if (tile.evasionBonus < 0) tooltip += `\n${tile.evasionBonus}% Evasion`;
  if (tile.defenseBonus > 0)  tooltip += `\n+${tile.defenseBonus}% Defense`;
  if (tile.hazardType)        tooltip += `\n⚠️ ${tile.hazardType} Hazard (${tile.hazardDamage} dmg/turn)`;
  if (!tile.isPassable)       tooltip += `\nImpassable`;
  if (tile.blocksLineOfSight) tooltip += `\nBlocks Vision`;
  return tooltip;
};

const BattleGridTiles: React.FC<BattleGridTilesProps> = ({
  gridWidth, gridHeight, tiles, units,
  selectedUnit, currentTurnUnit,
  validMoves, validTargets, skillTargets, skillRange, aoeArea, weaponRange,
  attackMode, skillMode, selectedSkill,
  itemTargets,
  onTileClick, onAoeAreaChange,
}) => {
  return (
    <>
      {Array.from({ length: gridHeight }, (_, y) => (
        <div key={y} className="grid-row">
          {Array.from({ length: gridWidth }, (_, x) => {
            const tile = getTileAtPosition(tiles, x, y);
            const unit = getUnitAtPosition(units, x, y);
            const isSelected = !!(selectedUnit && unit && selectedUnit.id === unit.id);
            const isValidMove = validMoves.some(m => m.x === x && m.y === y);
            const isValidTarget = !!(unit && validTargets.some(t => t.id === unit.id));
            const isSkillTarget = skillTargets.some(t => t.x === x && t.y === y);
            const isItemTarget = !!(unit && itemTargets.some(t => t.id === unit.id));
            const isSkillRangeTile = skillRange.some(r => r.x === x && r.y === y);
            const isAoeArea = aoeArea.some(a => a.x === x && a.y === y);
            const isCurrentTurnUnit = !!(unit && currentTurnUnit && unit.id === currentTurnUnit.unitId);
            const isWeaponRange = weaponRange.some(r => r.x === x && r.y === y);

            const handleTileHover = () => {
              if (attackMode && selectedUnit && isValidTarget) {
                const weapon = selectedUnit.equipment?.weapon;
                const aoeShape = weapon?.aoeShape || 'Single';
                onAoeAreaChange(calculateAOEPreview(selectedUnit.position, x, y, aoeShape));
              }
              if (skillMode && selectedSkill && isSkillTarget) {
                const aoe = selectedSkill.skill?.areaOfEffect || 0;
                if (aoe > 0) {
                  const aoeTiles: { x: number; y: number }[] = [{ x, y }];
                  for (let dx = -aoe; dx <= aoe; dx++) {
                    for (let dy = -aoe; dy <= aoe; dy++) {
                      if (dx === 0 && dy === 0) continue;
                      if (Math.abs(dx) + Math.abs(dy) <= aoe) aoeTiles.push({ x: x + dx, y: y + dy });
                    }
                  }
                  onAoeAreaChange(aoeTiles);
                } else {
                  onAoeAreaChange([{ x, y }]);
                }
              }
            };

            const handleTileLeave = () => {
              if (skillMode || attackMode) onAoeAreaChange([]);
            };

            const tileClass = [
              'grid-tile',
              isSelected ? 'selected' : '',
              unit ? 'has-unit' : '',
              isCurrentTurnUnit ? 'current-turn-unit' : '',
              isValidMove ? 'valid-move' : '',
              isValidTarget ? 'valid-target' : '',
              isSkillTarget ? 'skill-target' : '',
              isItemTarget ? 'item-target' : '',
              isSkillRangeTile ? 'skill-range' : '',
              isAoeArea ? 'aoe-area' : '',
              isWeaponRange ? 'weapon-range' : '',
              tile?.hazardType ? 'hazard-tile' : '',
            ].filter(Boolean).join(' ');

            const terrainSprite = tile ? getTerrainSprite(tile) : null;
            const baseColor    = tile ? getTileColor(tile) : '#333';

            return (
              <div
                key={`${x}-${y}`}
                className={tileClass}
                style={{
                  backgroundColor: baseColor,
                  backgroundImage: terrainSprite ? `url(${terrainSprite})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  imageRendering: 'pixelated',
                }}
                onClick={() => onTileClick(x, y)}
                onMouseEnter={handleTileHover}
                onMouseLeave={handleTileLeave}
                title={getTileTooltip(tile, x, y)}
              >
                <div className="tile-coords">{x},{y}</div>
                {tile?.hazardType && (
                  <div className="hazard-indicator" title={`${tile.hazardType} Hazard: ${tile.hazardDamage} damage/turn`}>
                    ⚠️
                  </div>
                )}
                {unit && (() => {
                  const spriteLayers = getUnitSpriteLayers(unit);
                  const hpPct     = unit.stats.maxHP > 0
                    ? Math.max(0, unit.stats.currentHP / unit.stats.maxHP)
                    : 0;
                  const hpColor   = getHealthColor(hpPct);
                  return (
                    <div className={`unit ${unit.isPlayerUnit ? 'player-unit' : 'enemy-unit'}`}>
                      {/* Backdrop - makes units readable against busy terrain */}
                      <div className="unit-backdrop" />

                      {/* Sprite (stacked layers: base body -> beard -> hair for players).
                          Each layer gets its own flex-centering wrap so layers overlay
                          exactly rather than laying out side-by-side. */}
                      {spriteLayers.map((layer, idx) => (
                        <div className="unit-sprite-wrap" key={`${layer.url}-${idx}`}>
                          <img
                            src={layer.url}
                            alt={unit.name}
                            className={`unit-sprite${unit.isPlayerUnit ? '' : ' unit-sprite--enemy'}`}
                            style={layer.filter ? ({ '--skin-tint': layer.filter } as React.CSSProperties) : undefined}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                            draggable={false}
                          />
                        </div>
                      ))}

                      {/* Name caption */}
                      <div className="unit-name">{unit.name}</div>

                      {/* HP bar */}
                      <div className="unit-hp-bar">
                        <div
                          className="unit-hp-fill"
                          style={{ width: `${hpPct * 100}%`, backgroundColor: hpColor }}
                        />
                      </div>

                      {/* Status effect icons */}
                      {unit.statusEffects && unit.statusEffects.length > 0 && (
                        <div className="unit-status-icons">
                          {unit.statusEffects.slice(0, 3).map((effect: StatusEffect, idx: number) => (
                            <span key={idx} className="status-icon" title={`${effect.effectName} (${effect.turnsRemaining} turns)`}>
                              {getEffectIcon(effect.effectType)}
                            </span>
                          ))}
                          {unit.statusEffects.length > 3 && (
                            <span className="status-icon-more" title={`+${unit.statusEffects.length - 3} more effects`}>
                              +{unit.statusEffects.length - 3}
                            </span>
                          )}
                        </div>
                      )}

                      <BattleUnitEquipment equipment={unit.equipment || {}} isPlayerUnit={unit.isPlayerUnit} />
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
};

export default BattleGridTiles;
